// Libraries
import { Box, Card, Flex, List, Text } from '@chakra-ui/react';
import { ReactElement } from 'react';
// Components
import { DataStateWrapper, StackBarChart } from '@molecules/index';
// Utils
import { formatNumberToDecimal } from '@/src/utils/helpers';
import text from '@translations/en.json';
import { transformContributionsIntoTickValues } from '@utils/index';
// Types
import { FocFactor } from '@globalTypes/index';
import { FocContributionFactorsProps } from './FocContributionFactors.type';

/**
 * A dictionary mapping the factors to their corresponding names.
 *
 * @type {Record<FocFactor, string>}
 */
const factorDictionary: Record<FocFactor, string> = {
  sea_temperature: text.sea_temperature,
  current: text.sea_current,
  wind: text.wind,
  wave: text.waves,
  fouling: text.fouling,
  speed: text.speed,
};

/**
 * An array of colors corresponding to each factor.
 *
 * @type {string[]}
 */
const factorColors: string[] = ['salmon', 'deepSkyBlue', 'blueViolet', 'lime', 'orange', 'tealishBlue'];

/**
 * Represents the FOC (Fuel Oil Consumption) contribution factors component.
 *
 * @component
 * @param {FocContributionFactorsProps} props - The component props.
 * @property {FocContributionFactor[]} focContributionFactors - The list of FOC contribution factors.
 * @property {string} [width='full'] - The width of the component.
 * @returns {ReactElement} The rendered FOC contribution factors component.
 */
export const FocContributionFactors = (props: FocContributionFactorsProps): ReactElement => {
  const { focContributionFactors, width = 'full' } = props;

  /**
   * Filters out the sea temperature factor and sorts the factors by value in ascending order.
   *
   * @type {FocContributionFactor[]}
   */
  const factorsDisplayed = focContributionFactors
  .filter(({ factor }) => factor != 'sea_temperature')
  .sort((a, b) => a.value - b.value);
  const roundedFactors = factorsDisplayed.map((factor) => ({ ...factor, value: Number(factor.value.toFixed(3)) }));
  const total = roundedFactors.reduce((acc, curr) => acc + curr.value, 0);
  const adjustment = 1 - total;
  const isEmpty = roundedFactors.length === 0;
  if (!isEmpty && Math.abs(adjustment) > 0) roundedFactors[roundedFactors.length - 1].value += Number(adjustment.toFixed(3));

  const { tickValues, negativeContributionFactors, positiveContributionFactors } =
    transformContributionsIntoTickValues(roundedFactors);


  return (
    <Card border='greyLight' h='full' data-testid='foc-contribution-factors'>
      <Flex w={width} maxW={113} direction='column' p={4}>
        <Text as='h3' fontSize='xl' fontWeight='semibold'>
          {text.foc_contribution_factors}
        </Text>
        <DataStateWrapper isEmpty={isEmpty}>
          <Box h={10} mt={4} mb={4}>
            <StackBarChart
              tickValues={tickValues}
              negativeContributionFactors={negativeContributionFactors}
              positiveContributionFactors={positiveContributionFactors}
            />
          </Box>

          <List>
            {roundedFactors.map(({ factor, value }, index) => (
              <Flex as='li' key={factor} mb={1}>
                <Flex flex={1} fontSize='m'>
                  <Box bg={factorColors[index]} h={2} w={2} borderRadius='full' my='auto' mr={2} />
                  {factorDictionary[factor] ?? factor}
                </Flex>
                <Text fontSize='m' color='grey.100'>
                  {`${formatNumberToDecimal(value * 100, 1, false)}%`}
                </Text>
              </Flex>
            ))}
          </List>
        </DataStateWrapper>
      </Flex>
    </Card>
  );
};
